import React, { useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { HighlightStyle, StreamLanguage, indentUnit, syntaxHighlighting } from "@codemirror/language";
import { EditorView, keymap } from "@codemirror/view";
import { Prec } from "@codemirror/state";
import { tags } from "@lezer/highlight";
import authService from "../../../utils/authService";
import type { NotebookPreviewCell } from "../../../utils/notebookPreview";
import { notebookRunnerUserMessage } from "../../../utils/notebookRunnerMessage";

type NotebookCell = NotebookPreviewCell;

interface ValidationCase {
  id: string;
  name: string;
  description?: string;
  input?: string;
  expected?: string;
  actual?: string;
  passed: boolean;
  error?: string;
}

interface ValidationReportData {
  passed: number;
  total: number;
  cases: ValidationCase[];
  message?: string;
  pending?: boolean;
  selectedId?: string;
}

interface OutputBlock {
  type: string;
  text?: string;
  mime?: string;
  data?: string;
  columns?: string[];
  rows?: string[][];
}

interface RunResult {
  ok: boolean;
  output: string;
  error: string;
  pending?: boolean;
  outputs?: OutputBlock[];
}

type Props = {
  title?: string;
  source: string;
  cells: NotebookCell[];
};

const LINE_BOX = 36;
const MIN_LINES = 3;
const COLLAPSED_HEIGHT = 16 + 20 * MIN_LINES;

const PYTHON_KEYWORDS = /^(?:False|None|True|and|as|assert|async|await|break|class|continue|def|del|elif|else|except|finally|for|from|global|if|import|in|is|lambda|nonlocal|not|or|pass|raise|return|try|while|with|yield)\b/;
const PYTHON_BUILTINS = new Set(["print", "len", "range", "int", "str", "float", "bool", "list", "dict", "set", "tuple", "type", "isinstance", "enumerate", "zip", "map", "filter", "sorted", "sum", "min", "max", "abs", "round", "open", "super"]);

const pythonHighlight = HighlightStyle.define([
  { tag: tags.keyword, color: "#cf222e", fontWeight: "650" },
  { tag: tags.special(tags.variableName), color: "#8250df" },
  { tag: tags.definition(tags.variableName), color: "#953800" },
  { tag: tags.string, color: "#0a3069" },
  { tag: tags.comment, color: "#6e7781", fontStyle: "italic" },
  { tag: tags.number, color: "#0550ae" },
  { tag: tags.operator, color: "#cf222e" },
]);

const pythonLanguage = StreamLanguage.define({
  tokenTable: {
    keyword: tags.keyword,
    builtin: tags.special(tags.variableName),
    def: tags.definition(tags.variableName),
    string: tags.string,
    comment: tags.comment,
    number: tags.number,
    operator: tags.operator,
  },
  startState() {
    return { quote: null as string | null };
  },
  token(stream, state) {
    if (state.quote) {
      const quote = state.quote;
      while (!stream.eol()) {
        const ch = stream.next();
        if (ch === "\\") {
          stream.next();
          continue;
        }
        if (quote.length === 1 && ch === quote) {
          state.quote = null;
          break;
        }
        if (quote.length === 3 && ch === quote[0] && stream.match(quote.slice(1))) {
          state.quote = null;
          break;
        }
      }
      return "string";
    }
    if (stream.eatSpace()) return null;
    if (stream.match("#")) {
      stream.skipToEnd();
      return "comment";
    }
    if (stream.match('"""') || stream.match("'''")) {
      state.quote = stream.current();
      return "string";
    }
    if (stream.match('"') || stream.match("'")) {
      state.quote = stream.current();
      return "string";
    }
    if (stream.match(/^(?:0x[\da-fA-F]+|\d+\.?\d*)/)) return "number";
    if (stream.match(PYTHON_KEYWORDS)) return "keyword";
    if (stream.match(/^[A-Za-z_]\w*/)) {
      if (PYTHON_BUILTINS.has(stream.current())) return "builtin";
      if (stream.match(/^\s*\(/, false)) return "def";
      return null;
    }
    if (stream.match(/^[+\-*/%=<>!&|^~]+/)) return "operator";
    stream.next();
    return null;
  },
});

const pythonTheme = EditorView.theme({
  "&": { fontSize: "13px", backgroundColor: "#fff" },
  ".cm-content": { fontFamily: "Consolas, ui-monospace, monospace", caretColor: "#1f2328" },
  ".cm-gutters": { backgroundColor: "#f6f8fa", color: "#8b949e", borderRight: "1px solid #d0d7de" },
  ".cm-activeLine": { backgroundColor: "#fff8c5" },
  ".cm-activeLineGutter": { backgroundColor: "#fff8c5" },
  "&.cm-focused": { outline: "none" },
});

function safeImage(data?: string) {
  return typeof data === "string" && data.length > 0 && data.length <= 1600000 && /^[A-Za-z0-9+/=\s]+$/.test(data);
}

function safeUrl(url: string) {
  const trimmed = String(url || "").trim();
  return /^(https?:|mailto:)/i.test(trimmed) ? trimmed : "";
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const pattern = /(`[^`\n]+`)|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let match = pattern.exec(text);
  let index = 0;
  while (match) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) {
      parts.push(
        <code key={`${keyPrefix}-c${index}`} style={{ background: "#f6f8fa", padding: "0 4px", borderRadius: 4, fontFamily: "Consolas, monospace" }}>
          {match[1].slice(1, -1)}
        </code>
      );
    } else if (match[2]) {
      parts.push(<strong key={`${keyPrefix}-b${index}`}>{match[3]}</strong>);
    } else if (match[4]) {
      parts.push(<em key={`${keyPrefix}-i${index}`}>{match[5]}</em>);
    } else if (match[6]) {
      const href = safeUrl(match[8]);
      parts.push(href
        ? <a key={`${keyPrefix}-a${index}`} href={href} target="_blank" rel="noreferrer">{match[7]}</a>
        : match[7]);
    }
    last = match.index + match[0].length;
    index += 1;
    match = pattern.exec(text);
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderMarkdown(source: string) {
  const lines = String(source || "").replace(/\r\n/g, "\n").split("\n");
  const blocks: React.ReactNode[] = [];
  let index = 0;
  let key = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (line.trim().startsWith("```")) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push(
        <pre key={key} style={{ margin: "8px 0", padding: 8, background: "#f6f8fa", borderRadius: 6, overflow: "auto" }}>
          <code style={{ fontFamily: "Consolas, monospace" }}>{code.join("\n")}</code>
        </pre>
      );
      key += 1;
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const Tag = level === 1 ? "h2" : level === 2 ? "h3" : "h4";
      const fontSize = level === 1 ? 22 : level === 2 ? 18 : 16;
      blocks.push(
        <Tag key={key} style={{ fontSize, margin: "8px 0", fontWeight: 650 }}>
          {renderInline(heading[2], `h${key}`)}
        </Tag>
      );
      key += 1;
      index += 1;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(<li key={index}>{renderInline(lines[index].replace(/^\s*[-*]\s+/, ""), `ul${key}-${index}`)}</li>);
        index += 1;
      }
      blocks.push(<ul key={key} style={{ margin: "8px 0", paddingLeft: 20 }}>{items}</ul>);
      key += 1;
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index])) {
        items.push(<li key={index}>{renderInline(lines[index].replace(/^\s*\d+\.\s+/, ""), `ol${key}-${index}`)}</li>);
        index += 1;
      }
      blocks.push(<ol key={key} style={{ margin: "8px 0", paddingLeft: 20 }}>{items}</ol>);
      key += 1;
      continue;
    }
    if (!line.trim()) {
      index += 1;
      continue;
    }
    const paragraph = [line];
    index += 1;
    while (
      index < lines.length
      && lines[index].trim()
      && !/^(#{1,3})\s+/.test(lines[index])
      && !/^\s*[-*]\s+/.test(lines[index])
      && !/^\s*\d+\.\s+/.test(lines[index])
      && !lines[index].trim().startsWith("```")
    ) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push(
      <p key={key} style={{ margin: "8px 0" }}>
        {renderInline(paragraph.join(" "), `p${key}`)}
      </p>
    );
    key += 1;
  }
  return blocks;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" style={{ transform: open ? "rotate(90deg)" : "none" }}>
      <path d="M4 2.2 8 6 4 9.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RunOutput({ result }: { result: RunResult }) {
  const outputs = Array.isArray(result.outputs) ? result.outputs : [];
  return (
    <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {result.pending && (
        <div style={{ fontSize: 12, color: "#57606a" }}>Running…</div>
      )}
      {!result.pending && !result.ok && (
        <pre style={{ margin: 0, padding: 10, borderRadius: 8, background: "#fff1f0", color: "#b42318", border: "1px solid #ffccc7", whiteSpace: "pre-wrap", fontSize: 12, fontFamily: "Consolas, monospace" }}>
          {notebookRunnerUserMessage(result.error)}
        </pre>
      )}
      {!result.pending && result.ok && outputs.length === 0 && (
        <div style={{ fontSize: 12, color: "#57606a" }}>{result.output || "(no output)"}</div>
      )}
      {outputs.map((block, index) => {
        if (block.type === "image" && safeImage(block.data)) {
          return (
            <img
              key={`image-${index}`}
              alt="Cell output"
              src={`data:${block.mime || "image/png"};base64,${block.data}`}
              style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid #d0d7de", background: "#fff" }}
            />
          );
        }
        if (block.type === "table" && Array.isArray(block.columns)) {
          const rows = Array.isArray(block.rows) ? block.rows : [];
          return (
            <div key={`table-${index}`} style={{ overflow: "auto", border: "1px solid #d0d7de", borderRadius: 8, background: "#fff" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "linear-gradient(90deg, #ddf4ff, #dafbe1)" }}>
                    {block.columns.map((column, columnIndex) => (
                      <th key={`${column}-${columnIndex}`} style={{ padding: "6px 8px", textAlign: "left", color: "#0969da", fontWeight: 650 }}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ background: rowIndex % 2 ? "#f6f8fa" : "#fff" }}>
                      {(Array.isArray(row) ? row : []).map((cell, cellIndex) => (
                        <td key={cellIndex} style={{ padding: "6px 8px", borderTop: "1px solid #d8dee4" }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === "text" && block.text) {
          return (
            <pre key={`text-${index}`} style={{ margin: 0, padding: 10, borderRadius: 8, background: "#f6f8fa", color: "#1f2328", borderLeft: "3px solid #1a7f37", whiteSpace: "pre-wrap", fontSize: 12, fontFamily: "Consolas, monospace" }}>
              {block.text}
            </pre>
          );
        }
        return null;
      })}
    </div>
  );
}

function ValidationReport({
  report,
  onSelect,
}: {
  report: ValidationReportData;
  onSelect: (id: string) => void;
}) {
  const cases = report.cases || [];
  const selected = cases.find((item) => item.id === report.selectedId) || cases[0];
  if (!cases.length && !report.message && !report.pending) return null;
  return (
    <div className="mt-2" style={{ border: "1px solid #d0d7de", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
      <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom" style={{ background: "#f6f8fa" }}>
        <strong style={{ fontSize: 12 }}>Validation</strong>
        <span className={`badge ${report.pending ? "bg-secondary" : report.total && report.passed === report.total ? "bg-success" : "bg-danger"}`}>
          {report.pending ? "Checking" : report.total ? `${report.passed}/${report.total} passed` : "No tests"}
        </span>
      </div>
      {report.pending && <div className="p-2 text-muted" style={{ fontSize: 12 }}>Running test cases…</div>}
      {report.message && !cases.length && !report.pending && (
        <div className="p-2 text-muted" style={{ fontSize: 12 }}>{notebookRunnerUserMessage(report.message, "Validation failed.")}</div>
      )}
      {cases.length > 0 && (
        <div className="d-flex" style={{ minHeight: 140 }}>
          <div className="border-end" style={{ width: 180, overflow: "auto", background: "#f8f9fa" }}>
            {cases.map((item) => (
              <button
                key={item.id}
                type="button"
                className="btn btn-sm w-100 text-start border-0 rounded-0"
                style={{ background: selected?.id === item.id ? "#fff" : "transparent" }}
                onClick={() => onSelect(item.id)}
              >
                <span className="d-flex justify-content-between align-items-center gap-2">
                  <span className="text-truncate" style={{ fontSize: 12 }}>{item.name}</span>
                  <span className={`badge ${item.passed ? "bg-success" : "bg-danger"}`}>{item.passed ? "Pass" : "Fail"}</span>
                </span>
              </button>
            ))}
          </div>
          {selected && (
            <div className="p-2" style={{ flex: 1, overflow: "auto", fontSize: 12 }}>
              <div className="mb-2">
                <strong>{selected.name}</strong>
                <span className={`badge ms-2 ${selected.passed ? "bg-success" : "bg-danger"}`}>
                  {selected.passed ? "Passed" : "Failed"}
                </span>
              </div>
              <div className="mb-2">
                <strong>Description: </strong>
                {selected.description || <span className="text-muted">No description</span>}
              </div>
              <div className="mb-2">
                <strong>Values</strong>
                <pre className="mb-0 mt-1 p-2 bg-light rounded" style={{ whiteSpace: "pre-wrap" }}>{selected.input || "No values"}</pre>
              </div>
              <div className="mb-2">
                <strong>Expected output</strong>
                <pre className="mb-0 mt-1 p-2 bg-light rounded" style={{ whiteSpace: "pre-wrap" }}>{selected.expected || "No expected output"}</pre>
              </div>
              <div className="mb-2">
                <strong>Actual output</strong>
                <pre className="mb-0 mt-1 p-2 bg-light rounded" style={{ whiteSpace: "pre-wrap" }}>{selected.actual || "No output"}</pre>
              </div>
              {selected.passed && (
                <div className="alert alert-success py-2 mb-0"><strong>Success: </strong>Output matches this test case.</div>
              )}
              {!selected.passed && (
                <div className="alert alert-danger py-2 mb-0"><strong>Error: </strong>{notebookRunnerUserMessage(selected.error, "This test case did not pass.")}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CodeCellEditor({
  value,
  disabled,
  collapsed,
  onChange,
  onRun,
}: {
  value: string;
  disabled: boolean;
  collapsed: boolean;
  onChange: (source: string) => void;
  onRun: () => void;
}) {
  const extensions = useMemo(() => [
    pythonLanguage,
    pythonTheme,
    syntaxHighlighting(pythonHighlight),
    indentUnit.of("    "),
    Prec.high(keymap.of([
      {
        key: "Mod-Enter",
        run() {
          onRun();
          return true;
        },
      },
      {
        key: "Enter",
        run(view) {
          const line = view.state.doc.lineAt(view.state.selection.main.head);
          const indent = (line.text.match(/^[ \t]*/) || [""])[0];
          view.dispatch(view.state.replaceSelection(`\n${indent}`));
          return true;
        },
      },
    ])),
  ], [onRun]);

  return (
    <div style={{ border: "1px solid #d0d7de", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
      <CodeMirror
        value={String(value || "")}
        editable={!disabled}
        indentWithTab
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          highlightActiveLine: true,
          highlightActiveLineGutter: true,
          bracketMatching: true,
          closeBrackets: true,
          indentOnInput: true,
          autocompletion: false,
        }}
        theme="light"
        height={collapsed ? `${COLLAPSED_HEIGHT}px` : "auto"}
        minHeight={`${COLLAPSED_HEIGHT}px`}
        extensions={extensions}
        onChange={(next) => onChange(next)}
      />
    </div>
  );
}

export default function NotebookStudentPreview({ title, source, cells: initialCells }: Props) {
  const [cells, setCells] = useState<NotebookCell[]>(initialCells || []);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, RunResult>>({});
  const [message, setMessage] = useState("");
  const [collapsedById, setCollapsedById] = useState<Record<string, boolean>>({});
  const [validatingId, setValidatingId] = useState<string | null>(null);
  const [validationById, setValidationById] = useState<Record<string, ValidationReportData>>({});

  const updateSource = (id: string, next: string) => {
    setCells((current) => current.map((cell) => (cell.id === id ? { ...cell, source: next } : cell)));
  };

  const runCell = async (cellId: string) => {
    setRunningId(cellId);
    setMessage("");
    setResults((current) => ({
      ...current,
      [cellId]: { ok: true, output: "", error: "", pending: true },
    }));
    try {
      const response = await authService.post("content_creation/notebooks/preview/run/", {
        source,
        cell_id: cellId,
        cells,
      });
      setResults((current) => ({ ...current, [cellId]: response.data }));
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setResults((current) => ({
        ...current,
        [cellId]: { ok: false, output: "", error: notebookRunnerUserMessage(detail, "Run failed.") },
      }));
    } finally {
      setRunningId(null);
    }
  };

  const validateCell = async (cellId: string) => {
    setValidatingId(cellId);
    setMessage("");
    setValidationById((current) => ({
      ...current,
      [cellId]: { passed: 0, total: 0, cases: [], pending: true },
    }));
    try {
      const response = await authService.post("content_creation/notebooks/preview/validate/", {
        source,
        cell_id: cellId,
        cells,
      });
      const data = response.data as ValidationReportData;
      setValidationById((current) => ({
        ...current,
        [cellId]: {
          passed: data.passed || 0,
          total: data.total || 0,
          cases: data.cases || [],
          message: data.message,
          selectedId: data.cases?.[0]?.id,
        },
      }));
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setValidationById((current) => ({
        ...current,
        [cellId]: { passed: 0, total: 0, cases: [], message: notebookRunnerUserMessage(detail, "Validation failed.") },
      }));
    } finally {
      setValidatingId(null);
    }
  };

  return (
    <div className="h-100 d-flex flex-column" style={{ background: "#fff" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #eef0f3" }}>
        <div className="d-flex justify-content-between align-items-center" style={{ padding: "14px 16px" }}>
          <div className="d-flex align-items-center gap-2">
            <span
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#f4f6f8",
                color: "#243044",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="3" y="2" width="10" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#1c2434" }}>Notebook</span>
          </div>
        </div>
        <div style={{ padding: "4px 16px 16px" }}>
          <div style={{ fontSize: 15, fontWeight: 650, color: "#1c2434" }}>{title || "Notebook"}</div>
          <div style={{ marginTop: 2, fontSize: 13, color: "#8b95a5" }}>
            Student preview. Run and Validate use marimo-validator, the same as the student notebook.
          </div>
          {message && (
            <div style={{ marginTop: 2, fontSize: 13, color: "#5c6778" }}>{message}</div>
          )}
        </div>
      </div>
      <div className="flex-grow-1 overflow-auto" style={{ padding: "8px 12px 16px" }}>
        {cells.map((cell) => {
          const isCode = cell.type !== "markdown";
          const readOnly = !!cell.read_only;
          const collapsed = !!collapsedById[cell.id];
          const result = results[cell.id];
          return (
            <fieldset
              key={cell.id}
              style={{
                position: "relative",
                margin: "0 0 12px",
                border: "1px solid #d0d7de",
                borderLeft: `3px solid ${isCode ? "#1a7f37" : "#8250df"}`,
                borderRadius: 8,
                background: isCode ? "#f6f8fa" : "#fff",
                padding: "14px 12px 12px",
              }}
            >
              <legend
                style={{
                  float: "none",
                  width: "auto",
                  margin: 0,
                  marginLeft: 8,
                  padding: "0 6px",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  color: isCode ? "#1a7f37" : "#8250df",
                  background: isCode ? "#f6f8fa" : "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <button
                  type="button"
                  aria-label={collapsed ? "Expand cell" : "Collapse cell"}
                  aria-expanded={!collapsed}
                  onClick={() => setCollapsedById((prev) => ({ ...prev, [cell.id]: !prev[cell.id] }))}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#57606a",
                    width: 16,
                    height: 16,
                    padding: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChevronIcon open={!collapsed} />
                </button>
                {isCode ? "Code" : "Markdown"}
              </legend>
              {isCode && (
                <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={validatingId === cell.id || runningId === cell.id}
                    onClick={() => validateCell(cell.id)}
                  >
                    {validatingId === cell.id ? "Validating" : "Validate"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    disabled={runningId === cell.id || validatingId === cell.id}
                    onClick={() => runCell(cell.id)}
                  >
                    {runningId === cell.id ? "Running" : "Run"}
                  </button>
                </div>
              )}
              {isCode ? (
                <CodeCellEditor
                  value={cell.source || ""}
                  disabled={readOnly}
                  collapsed={collapsed}
                  onChange={(next) => updateSource(cell.id, next)}
                  onRun={() => runCell(cell.id)}
                />
              ) : (
                <div
                  style={{
                    minHeight: collapsed ? COLLAPSED_HEIGHT : LINE_BOX,
                    maxHeight: collapsed ? COLLAPSED_HEIGHT : undefined,
                    overflow: collapsed ? "hidden" : "visible",
                    padding: "4px 2px",
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  {renderMarkdown(cell.source || "")}
                </div>
              )}
              {isCode && result && <RunOutput result={result} />}
              {isCode && validationById[cell.id] && (
                <ValidationReport
                  report={validationById[cell.id]}
                  onSelect={(testId) => setValidationById((prev) => ({
                    ...prev,
                    [cell.id]: { ...prev[cell.id], selectedId: testId },
                  }))}
                />
              )}
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
